import { useEffect, useRef, useState } from 'react';
import { ArrowDownIcon, ArrowUpIcon } from '@heroicons/react/24/solid';
import { PhotoIcon } from '@heroicons/react/24/outline';
import { Calendar, MapPin, ChevronDown, Trash2, Pencil, Search } from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  createTrip,
  updateTrip,
  uploadTripCover,
  deleteTripCover,
  deleteTrip,
  getTripById,
} from '../../moduleServices/travel_services';
import Spinner from '../../moduleComponents/SpinnerLoad';
import CountryBadge from '../../moduleComponents/CountryBadge';
import DropdownCountrySelect from '../../moduleComponents/DropdownCountrySelect';
import DatePicker from 'shared_items/DatePicker';
import { formatDate } from 'shared_items/formatConventions';
import { findCountryData, initializeCountryRegions } from '../../moduleServices/trips_sanitize';
import { sortPlacesByArrival } from '../../moduleServices/travel_utils';
import useTravelDateSortDescending from '../../moduleServices/useTravelDateSortDescending';

const emptyForm = () => ({
  title: '',
  description: '',
  date_range: { start: '', end: '' },
  google_photos_url: '',
  places: [],
});

const formFromTrip = (t, placesDescending = true) => ({
  title: t.title || '',
  description: t.description || '',
  date_range: { start: t.start_date || '', end: t.end_date || '' },
  google_photos_url: t.google_photos_url || '',
  places: sortPlacesByArrival(t.places || [], placesDescending).map((place) => ({
    ...place,
    date_range: { start: place.arrival_date || '', end: place.departure_date || '' },
  })),
});

/**
 * Trip overview panel for drawer embedding: create (trip=null), view + edit existing.
 * No modal chrome — content only.
 */
const TripOverviewPanel = ({
  trip,
  onSaved,
  onDeleted,
  onRequestDelete,
  onCancelCreate,
  readOnlyPlaceholder = false,
}) => {
  const isCreate = trip === null && !readOnlyPlaceholder;
  const [isEditMode, setIsEditMode] = useState(isCreate);
  const [formData, setFormData] = useState(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [regionsReady, setRegionsReady] = useState(false);
  const [coverPreview, setCoverPreview] = useState(null);
  const [pendingCoverFile, setPendingCoverFile] = useState(null);
  const [placesSortDescending, setPlacesSortDescending] = useTravelDateSortDescending(true);
  const [editingPhotosUrl, setEditingPhotosUrl] = useState(false);
  const coverFileRef = useRef(null);

  const formatTripDatesLabel = (start, end) => {
    const fa = start ? formatDate(start) : '';
    const fd = end ? formatDate(end) : '';
    if (!fa && !fd) return null;
    if (fa && fd) {
      if (start === end) return fa;
      return `${fa} to ${fd}`;
    }
    return fa || fd;
  };

  const openGooglePhotos = (url) => {
    if (!url?.trim()) return;
    window.open(url.trim(), '_blank', 'noopener,noreferrer');
  };

  const handleTogglePlacesSort = () => {
    setPlacesSortDescending((prev) => {
      const next = !prev;
      setFormData((fd) => ({
        ...fd,
        places: sortPlacesByArrival(fd.places || [], next),
      }));
      return next;
    });
  };

  const placesSortButton = (
    <button
      type="button"
      onClick={handleTogglePlacesSort}
      className="flex items-center gap-1 text-sm text-gray-600 hover:text-black"
    >
      {placesSortDescending ? (
        <>
          <ArrowDownIcon className="h-4 w-4" />
          Newer
        </>
      ) : (
        <>
          <ArrowUpIcon className="h-4 w-4" />
          Older
        </>
      )}
    </button>
  );

  useEffect(() => {
    initializeCountryRegions().then(() => setRegionsReady(true));
  }, []);

  useEffect(() => {
    setShowDeleteConfirm(false);
    setPendingCoverFile(null);
    setEditingPhotosUrl(false);
    if (!trip) {
      setIsEditMode(true);
      setFormData(emptyForm());
      setCoverPreview(null);
    } else {
      setIsEditMode(false);
      setFormData(formFromTrip(trip, placesSortDescending));
      setCoverPreview(trip.cover_url || null);
    }
    // Intentionally omit placesSortDescending — trip change only; sort is shared preference
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trip]);

  useEffect(() => {
    if (!isEditMode) return;
    setFormData((fd) => ({
      ...fd,
      places: sortPlacesByArrival(fd.places || [], placesSortDescending),
    }));
  }, [placesSortDescending, isEditMode]);

  if (readOnlyPlaceholder && !trip) {
    return (
      <div className="p-3 text-sm text-gray-500">
        No trip linked. Link a trip above to see cover, dates and places.
      </div>
    );
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'date_range') {
      setFormData((prev) => ({ ...prev, date_range: value }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handlePlaceChange = (index, field, value) => {
    const updatedPlaces = [...formData.places];
    updatedPlaces[index] = { ...updatedPlaces[index], [field]: value };
    setFormData((prev) => ({ ...prev, places: updatedPlaces }));
  };

  const handleAddPlace = () => {
    setFormData((prev) => ({
      ...prev,
      places: [...(prev.places || []), { name: '', country: '', date_range: { start: '', end: '' } }],
    }));
  };

  const handlePlaceDateRangeChange = (index, dateRange) => {
    const updatedPlaces = [...formData.places];
    updatedPlaces[index] = { ...updatedPlaces[index], date_range: dateRange };
    setFormData((prev) => ({ ...prev, places: updatedPlaces }));
  };

  const handleRemovePlace = (index) => {
    const updatedPlaces = [...formData.places];
    updatedPlaces.splice(index, 1);
    setFormData((prev) => ({ ...prev, places: updatedPlaces }));
  };

  const handleEditToggle = () => {
    setFormData(formFromTrip(trip, placesSortDescending));
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    if (isCreate) {
      if (onCancelCreate) {
        onCancelCreate();
        return;
      }
      setFormData(emptyForm());
      setPendingCoverFile(null);
      setCoverPreview(null);
    } else {
      setFormData(formFromTrip(trip, placesSortDescending));
      setCoverPreview(trip.cover_url || null);
      setPendingCoverFile(null);
      setIsEditMode(false);
    }
  };

  const buildPayload = () => ({
    title: formData.title,
    description: formData.description,
    start_date: formData.date_range?.start || '',
    end_date: formData.date_range?.end || '',
    google_photos_url: formData.google_photos_url,
    places: (formData.places || []).map((place) => ({
      name: place.name || '',
      country: place.country || '',
      arrival_date: place.date_range?.start || '',
      departure_date: place.date_range?.end || '',
    })),
  });

  const handleCoverFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const okTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!okTypes.includes(file.type)) {
      toast.error('Use JPEG, PNG or WebP');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be at most 5 MB');
      return;
    }
    setPendingCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  };

  const handleRemoveCover = async () => {
    if (isCreate || !trip?.id) {
      setPendingCoverFile(null);
      setCoverPreview(null);
      return;
    }
    if (!trip.cover_url && !trip.cover_object_path) {
      setPendingCoverFile(null);
      setCoverPreview(null);
      return;
    }
    setIsSubmitting(true);
    try {
      const updated = await deleteTripCover(trip.id);
      setPendingCoverFile(null);
      setCoverPreview(updated.cover_url || null);
      toast.success('Cover removed');
      onSaved?.(updated);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove cover');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = buildPayload();
      let tripId = trip?.id;
      if (isCreate) {
        const created = await createTrip(payload);
        tripId = created?.data?.id;
        toast.success('Trip created successfully!');
      } else {
        await updateTrip(trip.id, payload);
        toast.success('Trip updated successfully!');
      }
      if (pendingCoverFile && tripId) {
        await uploadTripCover(tripId, pendingCoverFile);
        toast.success('Cover uploaded');
      }
      const refreshed = tripId ? await getTripById(tripId) : null;
      setPendingCoverFile(null);
      if (refreshed) {
        setFormData(formFromTrip(refreshed));
        setCoverPreview(refreshed.cover_url || null);
        if (!isCreate) setIsEditMode(false);
        onSaved?.(refreshed);
      } else {
        onSaved?.(null);
      }
    } catch (error) {
      toast.error(
        error.response?.data?.detail
          || error.response?.data?.message
          || 'Failed to save trip'
      );
      console.error('Failed to save trip:', error.response?.data || error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!trip?.id) return;
    setShowDeleteConfirm(false);
    setIsSubmitting(true);
    try {
      await deleteTrip(trip.id);
      toast.success('Trip deleted successfully!');
      onDeleted?.(trip.id);
    } catch (error) {
      toast.error('Failed to delete trip');
      console.error('Failed to delete trip:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const showInlineDelete = !onRequestDelete && !isCreate;

  return (
    <div
      className="relative flex flex-col flex-1 min-h-0 overflow-hidden text-sm"
      data-regions-loaded={regionsReady}
    >
      {isSubmitting && (
        <div className="absolute inset-0 flex items-center justify-center z-20 bg-white/90">
          <div className="rounded-xl shadow-lg p-4 border border-gray-200">
            <Spinner />
          </div>
        </div>
      )}

      {!isEditMode && !isCreate ? (
        <div className="relative flex-1 overflow-y-auto p-3 text-xs space-y-4">
          <button
            type="button"
            onClick={handleEditToggle}
            className="absolute top-2 right-2 p-1.5 text-gray-600 hover:bg-gray-100 rounded-lg z-10"
            title="Edit"
          >
            <Pencil className="h-4 w-4" />
          </button>

          <div className="flex flex-row gap-4 pr-8">
            <div className="flex flex-col items-center gap-2 flex-shrink-0">
              <div className="w-28 h-28 bg-gray-100 rounded-md overflow-hidden border border-gray-200 flex flex-col items-center justify-center text-gray-400">
                {trip.cover_url ? (
                  <img src={trip.cover_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <>
                    <PhotoIcon className="h-7 w-7" />
                    <span className="text-[10px] mt-1">No image</span>
                  </>
                )}
              </div>
              {trip.google_photos_url ? (
                <button
                  type="button"
                  onClick={() => openGooglePhotos(trip.google_photos_url)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium text-gray-600 bg-gray-50 border border-gray-200 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                  title="Open Google Photos"
                >
                  <Search className="h-3.5 w-3.5" strokeWidth={2} />
                  Photos
                </button>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium text-gray-300 bg-gray-50 border border-gray-100 cursor-default">
                  <Search className="h-3.5 w-3.5" strokeWidth={2} />
                  Photos
                </span>
              )}
            </div>

            <div className="flex-1 min-w-0 space-y-1.5">
              <h3 className="text-xl font-semibold text-gray-900 leading-snug">
                {trip.title || 'Untitled Trip'}
              </h3>
              <div className="flex items-center gap-1.5 text-sm text-gray-600">
                <Calendar className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                <span>
                  {formatTripDatesLabel(trip.start_date, trip.end_date) || 'No dates'}
                </span>
              </div>
              <p className="text-sm text-gray-500 whitespace-pre-line">
                {trip.description?.trim() || 'No description'}
              </p>
            </div>
          </div>

          <div className="flex justify-between items-center pt-1">
            <h4 className="text-sm font-semibold">Places Visited</h4>
            {placesSortButton}
          </div>
          {trip.places && trip.places.length > 0 ? (
            <div className="space-y-2">
              {sortPlacesByArrival(trip.places, placesSortDescending).map((place, index) => (
                <div key={index} className="border border-gray-200 p-2 rounded-lg space-y-2 bg-gray-50/30">
                  <div className="flex gap-3 items-center">
                    <div className="w-[38%] min-w-0">
                      {(() => {
                        const cd = place.country ? findCountryData(place.country, 'iso3') : null;
                        const value = place.country;
                        const display = value ? (cd ? cd.iso3 : value) : null;
                        return (
                          <div className="flex items-center gap-2 w-full rounded-lg px-2 py-1.5 text-left text-xs bg-gray-100">
                            {cd?.iso2 ? (
                              <CountryBadge code={cd.iso2} size={18} />
                            ) : value ? (
                              <span className="w-[18px] flex-shrink-0" aria-hidden />
                            ) : null}
                            <span
                              className={`flex-1 truncate ${display ? 'text-gray-900 font-medium' : 'text-gray-400'}`}
                            >
                              {display || 'Select country'}
                            </span>
                            <ChevronDown
                              className="h-4 w-4 text-gray-500 flex-shrink-0 invisible"
                              aria-hidden
                            />
                          </div>
                        );
                      })()}
                    </div>
                    <div className="flex-1 min-w-0 flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-gray-100">
                      <MapPin className="w-4 h-4 text-gray-500 flex-shrink-0" aria-hidden />
                      <span
                        className={`flex-1 min-w-0 truncate text-xs ${place.name?.trim() ? 'text-gray-900' : 'text-gray-400'}`}
                      >
                        {place.name?.trim() || 'Place name'}
                      </span>
                    </div>
                  </div>
                  <div
                    className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-gray-100 w-full text-xs"
                    aria-label="Place dates"
                  >
                    <Calendar className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    <span className={place.arrival_date || place.departure_date ? 'text-gray-800' : 'text-gray-400'}>
                      {(() => {
                        const a = place.arrival_date;
                        const d = place.departure_date;
                        const fa = a ? formatDate(a) : '';
                        const fd = d ? formatDate(d) : '';
                        if (!fa && !fd) return 'Select place dates';
                        if (fa && fd) {
                          if (a === d) return <strong>{fa}</strong>;
                          return (
                            <>
                              <strong>{fa}</strong> to <strong>{fd}</strong>
                            </>
                          );
                        }
                        if (fa) return <strong>{fa}</strong>;
                        return <strong>{fd}</strong>;
                      })()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No places registered</p>
          )}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden min-h-0">
          <div className="flex-1 overflow-y-auto p-3 space-y-4 text-xs">
            <div className="flex flex-row gap-4">
              <div className="flex flex-col items-center gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => coverFileRef.current?.click()}
                  className="w-28 h-28 bg-gray-100 rounded-md overflow-hidden border border-gray-200 flex flex-col items-center justify-center text-gray-400 hover:bg-gray-50 hover:border-gray-300 transition-colors"
                  title="Change cover"
                >
                  {coverPreview ? (
                    <img src={coverPreview} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <>
                      <PhotoIcon className="h-7 w-7" />
                      <span className="text-[10px] mt-1">Add cover</span>
                    </>
                  )}
                </button>
                <input
                  ref={coverFileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleCoverFileChange}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => setEditingPhotosUrl((v) => !v)}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                    formData.google_photos_url
                      ? 'text-gray-700 bg-gray-50 border-gray-200 hover:bg-gray-100'
                      : 'text-gray-500 bg-white border-dashed border-gray-300 hover:border-gray-400 hover:text-gray-700'
                  }`}
                  title="Google Photos link"
                >
                  <Search className="h-3.5 w-3.5" strokeWidth={2} />
                  Photos
                </button>
                {(coverPreview || pendingCoverFile) && (
                  <button
                    type="button"
                    onClick={handleRemoveCover}
                    className="text-[10px] text-red-600 hover:underline"
                  >
                    Remove cover
                  </button>
                )}
                {editingPhotosUrl && (
                  <input
                    type="url"
                    name="google_photos_url"
                    value={formData.google_photos_url}
                    onChange={handleChange}
                    placeholder="Google Photos URL"
                    className="w-28 text-[10px] border border-gray-300 rounded-lg px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-gray-400"
                  />
                )}
              </div>

              <div className="flex-1 min-w-0 space-y-2">
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Trip title"
                  className="w-full text-xl font-semibold text-gray-900 border-0 border-b border-transparent hover:border-gray-200 focus:border-gray-400 focus:ring-0 px-0 py-0.5 placeholder:text-gray-300 placeholder:font-medium"
                  required
                />
                <DatePicker
                  name="date_range"
                  value={formData.date_range}
                  onChange={handleChange}
                  mode="range"
                  colorScheme="gray"
                  placeholder="Select trip dates"
                  className="rounded-lg w-full bg-gray-100 hover:bg-gray-200 border-0"
                  dropdownZIndex={70}
                />
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Description"
                  className="w-full text-sm text-gray-600 border border-gray-200 rounded-lg px-2.5 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-gray-500 placeholder:text-gray-300"
                />
              </div>
            </div>

            <div className="flex justify-between items-center pt-1 gap-2">
              <h4 className="text-sm font-semibold">Places Visited</h4>
              <div className="flex items-center gap-3">
                {placesSortButton}
                <button type="button" onClick={handleAddPlace} className="text-xs text-blue-600 hover:underline">
                  + Add Place
                </button>
              </div>
            </div>
            <div className="space-y-2 pb-1">
              {(formData.places || []).map((place, index) => (
                <div key={index} className="border border-gray-200 p-2 rounded-lg space-y-2 relative bg-gray-50/30">
                  <button
                    type="button"
                    onClick={() => handleRemovePlace(index)}
                    className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                    aria-label="Remove place"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                  <div className="flex gap-3 items-center">
                    <div className="w-[38%] min-w-0">
                      <DropdownCountrySelect
                        value={place.country || ''}
                        onChange={(iso3) => handlePlaceChange(index, 'country', iso3)}
                        placeholder="Select country"
                        className="w-full"
                      />
                    </div>
                    <div className="flex-1 min-w-0 flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-gray-100">
                      <MapPin className="w-4 h-4 text-gray-500 flex-shrink-0 pointer-events-none" aria-hidden />
                      <input
                        type="text"
                        value={place.name || ''}
                        onChange={(e) => handlePlaceChange(index, 'name', e.target.value)}
                        placeholder="Place name"
                        className="flex-1 min-w-0 bg-transparent border-0 p-0 text-xs text-gray-900 placeholder:text-gray-400 focus:ring-0 focus:outline-none"
                      />
                    </div>
                  </div>
                  <div>
                    <DatePicker
                      name={`place_date_range_${index}`}
                      value={place.date_range || { start: '', end: '' }}
                      onChange={(e) => handlePlaceDateRangeChange(index, e.target.value)}
                      mode="range"
                      colorScheme="gray"
                      placeholder="Select place dates"
                      className="rounded-lg w-full bg-gray-100 hover:bg-gray-200 border-0"
                      dropdownZIndex={70}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-between items-center gap-2 px-3 py-2 border-t border-gray-200 flex-shrink-0 bg-white sticky bottom-0 z-10">
            {showInlineDelete && (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="px-3 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
                disabled={isSubmitting}
              >
                Delete
              </button>
            )}
            <div className="flex gap-2 ml-auto">
              <button
                type="button"
                onClick={handleCancelEdit}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm bg-gray-700 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </form>
      )}

      {showDeleteConfirm && !isSubmitting && (
        <div className="absolute inset-0 z-[60] bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white p-6 rounded-2xl shadow-xl w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Confirm deletion</h3>
            <p className="mb-6 text-sm text-gray-700">
              This will delete <strong>&quot;{trip?.title || formData.title}&quot;</strong> and all associated places. Are you sure?
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="px-4 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white shadow"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TripOverviewPanel;
